import { FaTruck, FaUsers, FaAward, FaHandshake, FaBullseye, FaEye, FaShieldAlt } from 'react-icons/fa'
import { scrollToSection } from '../utils/scroll'
import { useRef } from 'react'
import VariableProximity from '../components/VariableProximity'

const About = () => {
  const aboutHeadingRef = useRef(null)
  const ourStoryRef = useRef(null)
  const missionHeadingRef = useRef(null)
  const visionHeadingRef = useRef(null)
  const coreValuesRef = useRef(null)
  const whyDifferentRef = useRef(null)
  const values = [
    {
      icon: <FaHandshake className="text-4xl text-accent-400" />,
      title: 'Integrity',
      description: 'We build trust through honest communication and transparent business practices.',
    },
    {
      icon: <FaBullseye className="text-4xl text-accent-400" />,
      title: 'Excellence',
      description: 'We strive for excellence in every load we dispatch and every relationship we build.',
    },
    {
      icon: <FaUsers className="text-4xl text-accent-400" />,
      title: 'Partnership',
      description: 'We view our clients as partners, working together to achieve mutual success.',
    },
    {
      icon: <FaAward className="text-4xl text-accent-400" />,
      title: 'Innovation',
      description: 'We leverage cutting-edge technology to stay ahead in the logistics industry.',
    },
    {
      icon: <FaEye className="text-4xl text-accent-400" />,
      title: 'Transparency',
      description: 'We maintain complete openness in all our operations, ensuring you always know where your loads stand and how rates are negotiated.',
    },
    {
      icon: <FaShieldAlt className="text-4xl text-accent-400" />,
      title: 'Trust',
      description: 'We earn your confidence through consistent performance, reliable service, and unwavering commitment to your success.',
    },
  ]

  return (
    <div>
      {/* Header */}
      <section className="section-padding bg-transparent backdrop-blur-sm text-white pt-24 border-b border-dark-700/30">
        <div className="container-custom text-center">
          <div
            ref={aboutHeadingRef}
            style={{ position: 'relative' }}
            className="mb-4"
          >
            <VariableProximity
              label="About EW Logistics & IT Solution LLC"
              className="text-5xl font-bold"
              fromFontVariationSettings="'wght' 400, 'opsz' 9"
              toFontVariationSettings="'wght' 1000, 'opsz' 40"
              containerRef={aboutHeadingRef}
              radius={100}
              falloff="linear"
              style={{ color: 'white' }}
            />
          </div>
          <p className="text-xl text-gray-200 max-w-3xl mx-auto">
            We specialize in Freight Consultancy and strive to provide the best possible services to our carriers. 
            Our focus is on alleviating the pressure of load sourcing and negotiations, ensuring that your vehicle 
            is continuously moving with the most competitive rates in the industry.
          </p>
        </div>
      </section>

      {/* Company Story */}
      <section className="section-padding bg-transparent backdrop-blur-sm">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center mb-8">
              {/* Animated Truck */}
              <div className="mr-4">
                <div 
                  style={{
                    animation: 'truckFloat 2s ease-in-out infinite',
                    transform: 'translateZ(0)',
                    willChange: 'transform'
                  }}
                >
                  <FaTruck className="text-5xl text-accent-400 drop-shadow-lg" style={{ filter: 'drop-shadow(0 0 15px rgba(0, 255, 163, 0.5))' }} />
                </div>
              </div>
              <div
                ref={ourStoryRef}
                style={{ position: 'relative' }}
              >
                <VariableProximity
                  label="Our Story"
                  className="text-4xl font-bold bg-gradient-to-r from-white via-gray-200 to-white bg-clip-text text-transparent"
                  fromFontVariationSettings="'wght' 400, 'opsz' 9"
                  toFontVariationSettings="'wght' 1000, 'opsz' 40"
                  containerRef={ourStoryRef}
                  radius={100}
                  falloff="linear"
                />
              </div>
            </div>
            <div className="prose prose-lg max-w-none">
              <p className="text-lg text-gray-300 mb-4">
                EW Logistics & IT Solution LLC is a leading freight consultancy firm dedicated to providing exceptional services to carriers across the industry. We understand the challenges that independent carriers and fleet owners face in today's competitive market, and we are here to help you succeed.
              </p>
              <p className="text-lg text-gray-300 mb-4">
                Our mission is to alleviate the pressure of load sourcing and negotiations, ensuring that your vehicle is continuously moving with the most competitive rates in the industry. We believe in building lasting partnerships with our carriers, treating each one as a valued partner rather than just another number.
              </p>
              <p className="text-lg text-gray-300">
                We would be thrilled to collaborate with you and look forward to the possibility of working together. With our comprehensive services, dedicated support, and commitment to your success, we are here to help your business thrive in the transportation industry.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="section-padding bg-transparent backdrop-blur-sm">
        <div className="container-custom">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            {/* Mission */}
            <div className="bg-gradient-to-br from-dark-800/80 to-dark-700/80 rounded-xl shadow-lg p-8 border border-dark-600/30">
              <div className="flex items-center mb-6">
                <FaBullseye className="text-4xl text-accent-400 mr-4" />
                <div
                  ref={missionHeadingRef}
                  style={{ position: 'relative' }}
                >
                  <VariableProximity
                    label="Our Mission"
                    className="text-3xl font-bold text-white"
                    fromFontVariationSettings="'wght' 400, 'opsz' 9"
                    toFontVariationSettings="'wght' 1000, 'opsz' 40"
                    containerRef={missionHeadingRef}
                    radius={100}
                    falloff="linear"
                  />
                </div>
              </div>
              <p className="text-lg text-gray-300 leading-relaxed">
                To provide exceptional freight consultancy services that help carriers maximize their earnings, minimize downtime, and build successful trucking businesses. We are committed to alleviating the pressure of load sourcing and negotiations, ensuring vehicles stay moving with the most competitive rates while treating every carrier as a valued partner.
              </p>
            </div>

            {/* Vision */}
            <div className="bg-gradient-to-br from-dark-800/80 to-dark-700/80 rounded-xl shadow-lg p-8 border border-dark-600/30">
              <div className="flex items-center mb-6">
                <FaEye className="text-4xl text-accent-400 mr-4" />
                <div
                  ref={visionHeadingRef}
                  style={{ position: 'relative' }}
                >
                  <VariableProximity
                    label="Our Vision"
                    className="text-3xl font-bold text-white"
                    fromFontVariationSettings="'wght' 400, 'opsz' 9"
                    toFontVariationSettings="'wght' 1000, 'opsz' 40"
                    containerRef={visionHeadingRef}
                    radius={100}
                    falloff="linear"
                  />
                </div>
              </div>
              <p className="text-lg text-gray-300 leading-relaxed">
                To become the most trusted freight consultancy firm in the industry, recognized for our commitment to carrier success, competitive rates, and exceptional service. We envision a future where every carrier has access to professional freight consultancy services that help them thrive, with no contract obligations and complete flexibility to grow their business on their terms.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Core Values */}
      <section className="section-padding bg-transparent backdrop-blur-sm">
        <div className="container-custom">
          <div className="text-center mb-12">
            <div
              ref={coreValuesRef}
              style={{ position: 'relative' }}
              className="mb-4"
            >
              <VariableProximity
                label="Our Core Values"
                className="text-4xl font-bold bg-gradient-to-r from-white via-gray-200 to-white bg-clip-text text-transparent"
                fromFontVariationSettings="'wght' 400, 'opsz' 9"
                toFontVariationSettings="'wght' 1000, 'opsz' 40"
                containerRef={coreValuesRef}
                radius={100}
                falloff="linear"
              />
            </div>
            <p className="text-xl text-gray-300 max-w-2xl mx-auto">
              These values guide everything we do and shape how we serve our clients.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {values.map((value, index) => (
              <div
                key={index}
                className="text-center p-6 bg-gradient-to-br from-dark-800/80 to-dark-700/80 rounded-xl border border-dark-600/30 transition-all duration-300 hover:scale-105 hover:-translate-y-2 hover:shadow-2xl hover:shadow-accent-400/20 hover:border-accent-400/50 cursor-pointer group"
              >
                <div className="flex justify-center mb-4 transition-transform duration-300 group-hover:scale-110 group-hover:rotate-3">{value.icon}</div>
                <h3 className="text-xl font-semibold text-white mb-3 transition-colors duration-300 group-hover:text-accent-400">
                  {value.title}
                </h3>
                <p className="text-gray-300 transition-colors duration-300 group-hover:text-gray-200">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why We Are Different */}
      <section className="section-padding bg-transparent backdrop-blur-sm text-white border-y border-dark-600/30">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto text-center">
            <div
              ref={whyDifferentRef}
              style={{ position: 'relative' }}
              className="mb-6"
            >
              <VariableProximity
                label="Why We Are Different"
                className="text-4xl font-bold bg-gradient-to-r from-white via-gray-200 to-white bg-clip-text text-transparent"
                fromFontVariationSettings="'wght' 400, 'opsz' 9"
                toFontVariationSettings="'wght' 1000, 'opsz' 40"
                containerRef={whyDifferentRef}
                radius={100}
                falloff="linear"
              />
            </div>
            <p className="text-xl text-gray-300 mb-8">
              At EW Logistics & IT Solution LLC, we do not just find loads—we build partnerships. Our team of 
              experienced freight consultants understands the trucking industry from the ground up. We specialize 
              in alleviating the pressure of load sourcing and negotiations, ensuring your vehicle stays moving 
              with competitive rates.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-12">
              <div className="bg-gradient-to-br from-dark-700/80 to-dark-600/80 backdrop-blur-sm rounded-lg p-6 border border-dark-500/30">
                <div className="text-3xl font-bold mb-2 bg-gradient-to-r from-white to-gray-300 bg-clip-text text-transparent">Best</div>
                <div className="text-gray-300">Competitive Rates</div>
              </div>
              <div className="bg-gradient-to-br from-dark-700/80 to-dark-600/80 backdrop-blur-sm rounded-lg p-6 border border-dark-500/30">
                <div className="text-3xl font-bold mb-2 bg-gradient-to-r from-white to-gray-300 bg-clip-text text-transparent">No</div>
                <div className="text-gray-300">Contract Obligations</div>
              </div>
              <div className="bg-gradient-to-br from-dark-700/80 to-dark-600/80 backdrop-blur-sm rounded-lg p-6 border border-dark-500/30">
                <div className="text-3xl font-bold mb-2 bg-gradient-to-r from-white to-gray-300 bg-clip-text text-transparent">24/7</div>
                <div className="text-gray-300">Dispatch Support</div>
              </div>
            </div>
          </div>
        </div>
      </section>

    </div>
  )
}

export default About

